import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap, Users, Trophy, Sparkles, Mail, Lock, Eye, EyeOff, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import ThemeToggle from '../components/ThemeToggle';
import { useTheme } from '../contexts/ThemeContext';

// Floating particles component for magical atmosphere
const FloatingParticles = () => {
  return (
    <div className="absolute inset-0 overflow-hidden pointer-events-none">
      {[...Array(30)].map((_, i) => (
        <div
          key={i}
          className="particle"
          style={{
            left: `${Math.random() * 100}%`,
            top: `${Math.random() * 100}%`,
            animationDelay: `${Math.random() * 5}s`,
            animationDuration: `${3 + Math.random() * 4}s`
          }}
        />
      ))}
    </div>
  );
};

// Styled Logo Component
const StyledLogo = () => {
  return (
    <div className="flex items-center gap-2">
      <div className="relative">
        {/* Pokeball icon */}
        <div className="w-10 h-10 rounded-full bg-gradient-to-b from-red-500 to-red-600 relative overflow-hidden shadow-lg">
          <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-white" />
          <div className="absolute top-1/2 left-0 right-0 h-1 bg-gray-800 -translate-y-1/2" />
          <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-3 h-3 bg-white rounded-full border-2 border-gray-800" />
        </div>
      </div>
      <div className="flex flex-col leading-none">
        <span className="text-2xl md:text-3xl font-outfit font-black">
          <span className="text-yellow-400 drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]" style={{ textShadow: '2px 2px 0 #1e40af, -1px -1px 0 #1e40af' }}>Belotto</span>
          <span className="text-blue-400 drop-shadow-[0_2px_2px_rgba(0,0,0,0.3)]" style={{ textShadow: '2px 2px 0 #eab308, -1px -1px 0 #eab308' }}> PokeClass</span>
        </span>
        <span className="text-xs md:text-sm font-bold text-blue-300 tracking-widest">CENTER</span>
      </div>
    </div>
  );
};

// Glassmorphism Login Card
const GlassLoginCard = ({ onNavigate }) => {
  const [showPassword, setShowPassword] = useState(false);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');

  const handleGoogleLogin = () => {
    onNavigate('/login');
  };

  return (
    <div className="glass-card w-full max-w-md p-8 rounded-3xl" data-testid="glass-login-card">
      <div className="text-center mb-6">
        <h3 className="text-2xl font-outfit font-bold text-white mb-2">Bem-vindo, Treinador!</h3>
        <p className="text-blue-200 text-sm">Entre para começar sua jornada</p>
      </div>

      <div className="space-y-4">
        {/* Email field with Pokeball icon */}
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2 w-5 h-5">
            <div className="w-full h-full rounded-full bg-gradient-to-b from-red-500 to-red-600 relative overflow-hidden">
              <div className="absolute bottom-0 left-0 right-0 h-1/2 bg-white" />
              <div className="absolute top-1/2 left-0 right-0 h-0.5 bg-gray-800 -translate-y-1/2" />
              <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-1.5 h-1.5 bg-white rounded-full border border-gray-800" />
            </div>
          </div>
          <Input
            type="email"
            placeholder="seu@email.com"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            className="glass-input pl-12 h-14 rounded-xl text-white placeholder:text-blue-300/50"
            data-testid="email-input"
          />
        </div>

        {/* Password field with Lightning icon */}
        <div className="relative">
          <div className="absolute left-4 top-1/2 -translate-y-1/2">
            <Zap className="w-5 h-5 text-yellow-400" />
          </div>
          <Input
            type={showPassword ? 'text' : 'password'}
            placeholder="Sua senha secreta"
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            className="glass-input pl-12 pr-12 h-14 rounded-xl text-white placeholder:text-blue-300/50"
            data-testid="password-input"
          />
          <button
            type="button"
            onClick={() => setShowPassword(!showPassword)}
            className="absolute right-4 top-1/2 -translate-y-1/2 text-blue-300 hover:text-white transition-colors"
          >
            {showPassword ? <EyeOff className="w-5 h-5" /> : <Eye className="w-5 h-5" />}
          </button>
        </div>

        {/* Login button with glow effect */}
        <Button
          onClick={handleGoogleLogin}
          className="glow-button w-full h-14 rounded-xl text-lg font-bold"
          data-testid="login-submit-btn"
        >
          <span className="flex items-center gap-2">
            Entrar na Aventura
            <ArrowRight className="w-5 h-5" />
          </span>
        </Button>

        {/* Divider */}
        <div className="flex items-center gap-4 my-4">
          <div className="flex-1 h-px bg-white/20" />
          <span className="text-blue-300 text-sm">ou</span>
          <div className="flex-1 h-px bg-white/20" />
        </div>

        {/* Google login button */}
        <Button
          onClick={handleGoogleLogin}
          variant="outline"
          className="w-full h-12 rounded-xl border-white/30 text-white hover:bg-white/10 font-semibold"
          data-testid="google-login-btn"
        >
          <svg className="w-5 h-5 mr-2" viewBox="0 0 24 24">
            <path fill="currentColor" d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"/>
            <path fill="currentColor" d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"/>
            <path fill="currentColor" d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"/>
            <path fill="currentColor" d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"/>
          </svg>
          Continuar com Google
        </Button>
      </div>
    </div>
  );
};

// Feature Card Component
const FeatureCard = ({ icon, title, description, color }) => {
  const colorClasses = {
    yellow: 'from-yellow-400 to-orange-500',
    blue: 'from-blue-400 to-indigo-500',
    green: 'from-green-400 to-emerald-500',
    purple: 'from-purple-400 to-pink-500'
  };

  return (
    <div className="feature-card group" data-testid={`feature-card-${title.toLowerCase().replace(/\s/g, '-')}`}>
      <div className={`w-16 h-16 rounded-2xl bg-gradient-to-br ${colorClasses[color]} flex items-center justify-center text-white mb-4 shadow-lg group-hover:scale-110 transition-transform duration-300`}>
        {icon}
      </div>
      <h3 className="text-xl font-outfit font-bold text-slate-900 dark:text-white mb-2">{title}</h3>
      <p className="text-slate-600 dark:text-slate-300 font-nunito leading-relaxed text-sm">{description}</p>
    </div>
  );
};

const Landing = () => {
  const navigate = useNavigate();
  const { isDark } = useTheme();

  return (
    <div className="landing-page min-h-screen relative overflow-hidden">
      {/* Animated gradient background */}
      <div className="absolute inset-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900" />
      
      {/* Floating particles */}
      <FloatingParticles />

      {/* Main content */}
      <div className="relative z-10">
        {/* Navigation */}
        <nav className="container mx-auto px-4 py-6">
          <div className="flex justify-between items-center">
            <StyledLogo />
            <div className="flex items-center gap-3">
              <ThemeToggle />
              <Button
                data-testid="nav-login-btn"
                onClick={() => navigate('/login')}
                className="bg-white/10 hover:bg-white/20 text-white border border-white/20 h-11 px-6 rounded-full font-semibold backdrop-blur-sm transition-all"
              >
                Login
              </Button>
            </div>
          </div>
        </nav>

        {/* Hero Section */}
        <section className="container mx-auto px-4 py-8 md:py-16">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            {/* Left side - Login Card */}
            <div className="order-2 lg:order-1 flex justify-center lg:justify-start">
              <GlassLoginCard onNavigate={navigate} />
            </div>

            {/* Right side - Hero content */}
            <div className="order-1 lg:order-2 text-center lg:text-left">
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-outfit font-black text-white mb-6 leading-tight">
                Educação{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-yellow-400 to-orange-500">
                  Gamificada
                </span>{' '}
                com{' '}
                <span className="text-transparent bg-clip-text bg-gradient-to-r from-blue-400 to-cyan-400">
                  Monstros
                </span>
              </h1>
              
              <p className="text-lg md:text-xl text-blue-200 font-nunito mb-8 leading-relaxed max-w-xl mx-auto lg:mx-0">
                Transforme suas aulas em aventuras épicas. Estudantes evoluem seus monstros
                completando tarefas e missões!
              </p>

              {/* Hero illustration */}
              <div className="relative mx-auto lg:mx-0 max-w-lg">
                <div className="absolute inset-0 bg-gradient-to-r from-blue-500/30 to-purple-500/30 rounded-3xl blur-3xl" />
                <img
                  src="https://static.prod-images.emergentagent.com/jobs/02852d35-fb68-4f29-aacc-d6becca24983/images/2c0eda676980f7cc3284bece1e383bec10c97a58eb81de87181372de7db06797.png"
                  alt="Pokemon Center"
                  className="relative rounded-3xl shadow-2xl border-4 border-white/20 w-full"
                  data-testid="hero-illustration"
                />
                
                {/* Floating badges */}
                <div className="absolute -top-4 -right-4 bg-yellow-400 text-yellow-900 px-4 py-2 rounded-full font-bold text-sm shadow-lg animate-bounce">
                  +100 AURA
                </div>
                <div className="absolute -bottom-4 -left-4 bg-green-400 text-green-900 px-4 py-2 rounded-full font-bold text-sm shadow-lg animate-pulse">
                  Level Up!
                </div>
              </div>
            </div>
          </div>
        </section>

        {/* Features Section */}
        <section className="container mx-auto px-4 py-16">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-outfit font-bold text-white mb-4">
              Por que escolher o <span className="text-yellow-400">PokeClass</span>?
            </h2>
            <p className="text-blue-200 font-nunito max-w-2xl mx-auto">
              Uma plataforma completa para transformar a educação em uma experiência divertida e engajadora.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <FeatureCard
              icon={<Zap className="w-8 h-8" />}
              title="Sistema de Evolução"
              description="Monstros evoluem nos níveis 16 e 32, desbloqueando novos poderes e habilidades especiais."
              color="yellow"
            />
            <FeatureCard
              icon={<Users className="w-8 h-8" />}
              title="3 Classes Únicas"
              description="Tank, Atacante ou Suporte. Cada classe com 15 criaturas diferentes e estratégias únicas."
              color="blue"
            />
            <FeatureCard
              icon={<Trophy className="w-8 h-8" />}
              title="Tarefas & AURA"
              description="Professores criam tarefas que convertem em AURA. Gamificação real na educação."
              color="green"
            />
          </div>
        </section>

        {/* CTA Section */}
        <section className="container mx-auto px-4 py-16">
          <div className="glass-card rounded-3xl p-8 md:p-12 text-center max-w-4xl mx-auto">
            <Sparkles className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-2xl md:text-3xl font-outfit font-bold text-white mb-4">
              Pronto para revolucionar suas aulas?
            </h3>
            <p className="text-blue-200 mb-8 font-nunito max-w-xl mx-auto">
              Junte-se a milhares de educadores que já estão transformando a educação com gamificação.
            </p>
            <Button
              data-testid="footer-cta-btn"
              onClick={() => navigate('/login')}
              className="glow-button h-14 px-12 rounded-full text-lg font-bold"
            >
              <span className="flex items-center gap-2">
                Começar Agora
                <ArrowRight className="w-5 h-5" />
              </span>
            </Button>
          </div>
        </section>

        {/* Footer */}
        <footer className="container mx-auto px-4 py-8 border-t border-white/10">
          <div className="flex flex-col md:flex-row justify-between items-center gap-4">
            <StyledLogo />
            <p className="text-blue-300 text-sm font-nunito">
              © 2026 Belotto PokeClass Center. Todos os direitos reservados.
            </p>
          </div>
        </footer>
      </div>

      {/* Custom styles */}
      <style jsx>{`
        .landing-page {
          font-family: 'Nunito', sans-serif;
        }

        /* Floating particles animation */
        .particle {
          position: absolute;
          width: 4px;
          height: 4px;
          background: rgba(255, 255, 255, 0.6);
          border-radius: 50%;
          animation: float linear infinite;
          box-shadow: 0 0 10px rgba(255, 255, 255, 0.5);
        }

        @keyframes float {
          0% {
            transform: translateY(0) translateX(0);
            opacity: 0;
          }
          10% {
            opacity: 1;
          }
          90% {
            opacity: 1;
          }
          100% {
            transform: translateY(-100vh) translateX(20px);
            opacity: 0;
          }
        }

        /* Glassmorphism card */
        .glass-card {
          background: rgba(255, 255, 255, 0.1);
          backdrop-filter: blur(20px);
          -webkit-backdrop-filter: blur(20px);
          border: 1px solid rgba(255, 255, 255, 0.2);
          box-shadow: 0 8px 32px rgba(0, 0, 0, 0.3);
        }

        /* Glass input */
        .glass-input {
          background: rgba(255, 255, 255, 0.1) !important;
          border: 1px solid rgba(255, 255, 255, 0.2) !important;
          backdrop-filter: blur(10px);
          -webkit-backdrop-filter: blur(10px);
        }

        .glass-input:focus {
          border-color: rgba(96, 165, 250, 0.5) !important;
          box-shadow: 0 0 20px rgba(96, 165, 250, 0.3) !important;
        }

        /* Glow button */
        .glow-button {
          background: linear-gradient(135deg, #3b82f6 0%, #1d4ed8 100%);
          box-shadow: 0 4px 15px rgba(59, 130, 246, 0.4);
          transition: all 0.3s ease;
        }

        .glow-button:hover {
          transform: translateY(-2px);
          box-shadow: 0 8px 25px rgba(59, 130, 246, 0.6);
        }

        /* Feature card */
        .feature-card {
          background: rgba(255, 255, 255, 0.95);
          border-radius: 1.5rem;
          padding: 2rem;
          box-shadow: 0 4px 20px rgba(0, 0, 0, 0.1);
          transition: all 0.3s ease;
          border: 1px solid rgba(255, 255, 255, 0.5);
        }

        .feature-card:hover {
          transform: translateY(-8px);
          box-shadow: 0 12px 40px rgba(0, 0, 0, 0.15);
        }

        /* Dark mode feature cards */
        [data-theme="dark"] .feature-card {
          background: rgba(30, 41, 59, 0.95);
          border-color: rgba(255, 255, 255, 0.1);
        }
      `}</style>
    </div>
  );
};

export default Landing;
